/**
 * Accès Airtable via le proxy WordPress (https://ambpicot.pro/api/airtable/).
 *
 * Le jeton Airtable reste côté serveur : l'application s'authentifie avec la
 * session obtenue à la connexion (/api/login/). Même API que la librairie
 * « airtable » : base("table").select().eachPage(), find, create, update, destroy.
 */
import api from "./api";

let sessionProvider = () => "";
let sessionExpiredHandler = () => {};

// Fournit le session_id de l'utilisateur connecté (réel, même en impersonation).
export function setSessionProvider(provider) {
	sessionProvider = provider;
}

// Appelé quand le serveur répond 401 (session expirée ou absente).
export function setSessionExpiredHandler(handler) {
	sessionExpiredHandler = handler;
}

function request(action, table, payload) {
	const body = new URLSearchParams();
	body.append("session_id", sessionProvider() || "");
	body.append("payload", JSON.stringify(Object.assign({ action, table }, payload)));

	// Une création n'est jamais rejouée (risque de doublon) : le client passe d'abord le challenge du pare-feu (libs/api.js).
	return api
		.post("/airtable/", body, { replayable: action !== "create" })
		.then((response) => response.data)
		.catch((error) => {
			const status = error.response ? error.response.status : 0;
			const data = error.response && error.response.data ? error.response.data : {};
			const err = new Error(data.error && data.error.message ? data.error.message : error.message);
			err.statusCode = status;
			err.error = data.error && data.error.type ? data.error.type : "NETWORK_ERROR";
			if (status === 401) {
				sessionExpiredHandler(err);
			}
			throw err;
		});
}

function toRecord(raw) {
	return {
		id: raw.id,
		fields: raw.fields || {},
		createdTime: raw.createdTime,
		getId() {
			return this.id;
		},
		get(name) {
			return this.fields[name];
		},
	};
}

// Style « airtable » : rappel (err, résultat) si fourni, sinon promesse.
function respond(promise, callback) {
	if (typeof callback === "function") {
		promise.then(
			(result) => callback(null, result),
			(err) => callback(err)
		);
		return undefined;
	}
	return promise;
}

function splitArgs(optsOrCallback, callback) {
	if (typeof optsOrCallback === "function") {
		return { opts: {}, callback: optsOrCallback };
	}
	return { opts: optsOrCallback || {}, callback };
}

function table(name) {
	return {
		select(params = {}) {
			const fetchPage = (offset) =>
				request("select", name, { params: Object.assign({}, params, offset ? { offset } : {}) });

			return {
				eachPage(pageCallback, doneCallback) {
					const promise = new Promise((resolve, reject) => {
						const run = (offset) => {
							fetchPage(offset).then((data) => {
								const records = (data.records || []).map(toRecord);
								const next = () => (data.offset ? run(data.offset) : resolve());
								pageCallback(records, next);
							}, reject);
						};
						run();
					});
					return respond(promise, doneCallback);
				},
				firstPage(callback) {
					const promise = fetchPage().then((data) => (data.records || []).map(toRecord));
					return respond(promise, callback);
				},
				all(callback) {
					const records = [];
					const promise = new Promise((resolve, reject) => {
						const run = (offset) => {
							fetchPage(offset).then((data) => {
								records.push(...(data.records || []).map(toRecord));
								if (data.offset) {
									run(data.offset);
								} else {
									resolve(records);
								}
							}, reject);
						};
						run();
					});
					return respond(promise, callback);
				},
			};
		},

		find(id, callback) {
			return respond(request("find", name, { id }).then(toRecord), callback);
		},

		// create([{ fields }], cb) ou create(fields, cb) ; option { typecast }.
		create(records, optsOrCallback, callback) {
			const args = splitArgs(optsOrCallback, callback);
			const isArray = Array.isArray(records);
			const list = (isArray ? records : [records]).map((record) => ({ fields: record && record.fields ? record.fields : record }));
			const promise = request("create", name, { records: list, typecast: !!args.opts.typecast }).then((data) => {
				const created = (data.records || []).map(toRecord);
				return isArray ? created : created[0];
			});
			return respond(promise, args.callback);
		},

		// update([{ id, fields }], cb) ou update(id, fields, cb) ; option { typecast }.
		update(recordsOrId, fieldsOrOpts, optsOrCallback, callback) {
			let list;
			let isArray = true;
			let args;
			if (typeof recordsOrId === "string") {
				isArray = false;
				list = [{ id: recordsOrId, fields: fieldsOrOpts || {} }];
				args = splitArgs(optsOrCallback, callback);
			} else {
				list = recordsOrId.map((record) => ({ id: record.id, fields: record.fields || {} }));
				args = splitArgs(fieldsOrOpts, optsOrCallback);
			}
			const promise = request("update", name, { records: list, typecast: !!args.opts.typecast }).then((data) => {
				const updated = (data.records || []).map(toRecord);
				return isArray ? updated : updated[0];
			});
			return respond(promise, args.callback);
		},

		// destroy(id, cb) ou destroy([ids], cb).
		destroy(idOrIds, callback) {
			const isArray = Array.isArray(idOrIds);
			const promise = request("destroy", name, { ids: isArray ? idOrIds : [idOrIds] }).then((data) => {
				const deleted = (data.records || []).map(toRecord);
				return isArray ? deleted : deleted[0];
			});
			return respond(promise, callback);
		},
	};
}

export default function base(name) {
	return table(name);
}
